import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { BASE_URL } from "../../../config";
import { getAuthHeaders } from "../../../services/useAuth";
import ProductSalesModal from "../Cosmacia/ProductSalesModal";
import GlobalFilter from "../../../Admin/Filters/GlobalFilter";
import Loader from "../../../dist/Loader";
import CustomButton from "../../../pages/CustomButton";
import formatDate from "../../../utils/formatDate";
const KPKBProductList = () => {
  const [states, setStates] = useState([{ id: "All", name: "All" }]);
  const [divisions, setDivisions] = useState([]);
  const [selectedDivision, setSelectedDivision] = useState("All");
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());

  const [selectedState, setSelectedState] = useState("All");

  const [tableData, setTableData] = useState([]);
  const [showContainers, setShowContainers] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const [totals, setTotals] = useState({ totalCount: 0, totalEmployees: 0 });

  useEffect(() => {
    const fetchStates = async () => {
      try {
        const res = await axios.get(`${BASE_URL}/api/states`, {
          headers: getAuthHeaders(),
        });

        const raw = res.data || [];
        const list = Array.isArray(raw) ? raw : raw.data || [];

        // prepend All
        setStates([{ id: "All", name: "All" }, ...list]);
      } catch (err) {
        console.error(err);
        setError("Failed to load states");
      }
    };
    fetchStates();
  }, []);

  useEffect(() => {
    if (!selectedState) {
      setDivisions([]);
      setSelectedDivision("All");
      return;
    }
    const fetchDivisions = async () => {
      try {
        const res = await axios.get(
          `${BASE_URL}/api/divisions?state_id=${selectedState}`,
          { headers: getAuthHeaders() }
        );
        setDivisions([{ id: "All", name: "All" }, ...(res.data || [])]);
        setSelectedDivision("All");
      } catch (err) {
        console.error(err);
        setError("Failed to load divisions");
      }
    };
    fetchDivisions();
  }, [selectedState]);

  const handleGoClick = async () => {
    setIsLoading(true);
    setError(null);
    setTableData([]);
    setTotals({ totalCount: 0, totalEmployees: 0 });

    try {
      const params = new URLSearchParams();

      // Only send state_id when not All
      if (selectedState && selectedState !== "All") {
        params.set("state_id", selectedState);
      }

      if (selectedDivision && selectedDivision !== "All") {
        const divisionName = divisions.find(
          (d) => String(d.id) === String(selectedDivision)
        )?.name;
        if (divisionName) {
          params.set("division", divisionName);
        }
      }

      if (selectedYear) {
        params.set("year", selectedYear);
      }

      if (selectedMonth) {
        params.set("month", String(selectedMonth).padStart(2, "0"));
      }

      const queryString = params.toString();
      const url = `${BASE_URL}/api/reports/employee-monthly-sales${
        queryString ? `?${queryString}` : ""
      }`;

      const res = await axios.get(url, { headers: getAuthHeaders() });

      const payload = res.data || {};
      let rows = [];

      if (Array.isArray(payload.data)) {
        rows = payload.data;
      } else if (Array.isArray(payload.employees)) {
        rows = payload.employees;
      } else if (payload.data && Array.isArray(payload.data.employees)) {
        rows = payload.data.employees;
      } else if (Array.isArray(payload)) {
        rows = payload;
      } else {
        const nested = Object.values(payload).find((v) => Array.isArray(v));
        if (nested) rows = nested;
      }

      const totalCount =
        payload.count ??
        payload.total_count ??
        payload.total ??
        (rows.length ? rows.length : 0);

      const totalEmployees =
        payload.employees_count ?? payload.total_employees ?? rows.length;

      setTableData(rows || []);
      setTotals({
        totalCount: totalCount || 0,
        totalEmployees: totalEmployees || rows.length || 0,
      });

      setShowContainers(true);
    } catch (err) {
      console.error(err);
      setError("Failed to fetch data");
      setTableData([]);
    } finally {
      setIsLoading(false);
    }
  };

  const months = [
    { value: 1, label: "Jan" },
    { value: 2, label: "Feb" },
    { value: 3, label: "Mar" },
    { value: 4, label: "Apr" },
    { value: 5, label: "May" },
    { value: 6, label: "Jun" },
    { value: 7, label: "Jul" },
    { value: 8, label: "Aug" },
    { value: 9, label: "Sep" },
    { value: 10, label: "Oct" },
    { value: 11, label: "Nov" },
    { value: 12, label: "Dec" },
  ];
  const years = Array.from(
    { length: 5 },
    (_, i) => new Date().getFullYear() - i
  );

  return (
    <div className="p-2  border-2 border-gray-300 rounded-lg bg-white">
      <GlobalFilter
        title="KPKB "
        subtitle="Product List"
        states={states}
        divisions={divisions}
        months={months}
        years={years}
        selectedState={selectedState}
        setSelectedState={setSelectedState}
        selectedDivision={selectedDivision}
        setSelectedDivision={setSelectedDivision}
        selectedMonth={selectedMonth}
        setSelectedMonth={setSelectedMonth}
        selectedYear={selectedYear}
        setSelectedYear={setSelectedYear}
        handleGoClick={handleGoClick}
        isLoading={isLoading}
        error={error}
        CustomButton={CustomButton}
         Loader={(props) => (
    <Loader {...props} onCancel={() => setIsLoading(false)} />
  )}
      />
      {showContainers && (
        <div className="mt-4">
          {isLoading ? (
            <div className="text-center p-4">Loading data...</div>
          ) : tableData.length > 0 ? (
            <MonthlyAttendanceTable
              data={tableData}
              month={selectedMonth}
              year={selectedYear}
            />
          ) : (
            <div className="text-center p-4">No data available</div>
          )}
        </div>
      )}
    </div>
  );
};

const MonthlyAttendanceTable = ({ data, month, year }) => {
  const tableRef = useRef(null);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);

  const weekdayLabels = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

  const getDayOfWeek = (year, month, day) => {
    const date = new Date(year, month - 1, day);
    return date.getDay();
  };

  const daysInMonth = new Date(year, month, 0).getDate();

  const colWidths = {
    serialNo: "40px",
    empId: "80px",
    state: "120px",
    employee: "180px",
    doj: "90px",
    dor: "90px",
    manager: "180px",
    designation: "140px",
    city: "120px",
    division: "120px",
    day: "30px",
    // total: "60px",
    actualCount: "80px",
  };

  const handleDayClick = (employeeId, day) => {
    const selectedDate = new Date(year, month - 1, day);
    setSelectedEmployee(employeeId);
    setSelectedDate(selectedDate);
  };

  const closeModal = () => {
    setSelectedEmployee(null);
    setSelectedDate(null);
  };

  const generateDayHeaders = () => {
    const headers = [];
    for (let day = 1; day <= daysInMonth; day++) {
      const dayOfWeek = getDayOfWeek(year, month, day);
      const isSunday = dayOfWeek === 0;
      headers.push(
        <th
          key={day}
          className={`text-center p-0.5 text-[11px] whitespace-nowrap w-[30px] ${
            isSunday ? "bg-green-200" : ""
          }`}
          scope="col"
        >
          <div>{day}</div>
          <div className="text-[9px]">{weekdayLabels[dayOfWeek]}</div>
        </th>
      );
    }
    return headers;
  };

  const generateDayCells = (rowData) => {
    const cells = [];
    for (let day = 1; day <= daysInMonth; day++) {
      const dayOfWeek = getDayOfWeek(year, month, day);
      const isSunday = dayOfWeek === 0;

      let dayValue = 0;
      if (rowData.daily_sales && rowData.daily_sales[day]) {
        dayValue = rowData.daily_sales[day].total_qty || 0;
      }

      cells.push(
        <td
          key={day}
          className={`text-center p-0.5 font-bold text-xs whitespace-nowrap w-[30px] ${
            isSunday ? "bg-green-200" : ""
          } ${dayValue > 0 ? "cursor-pointer" : "cursor-default"}`}
          onClick={() => dayValue > 0 && handleDayClick(rowData.emp_id, day)}
        >
          <a
            href="#"
            onClick={(e) => e.preventDefault()}
            className={
              dayValue > 0 ? "text-blue-700 hover:underline" : "text-gray-400"
            }
          >
            {dayValue}
          </a>
        </td>
      );
    }
    return cells;
  };

  return (
    <>
      <div className="border rounded shadow  table-wrapper">
        <table className="unlock-dcr-leave-table text-sm ">
          <thead className="bg-blue-500 text-white">

            <tr className="bg-[#cfeefb]">
              <th
                className="text-center p-1.5 text-[13px] w-[40px] border border-gray-300"
                scope="col"
              >
                S.No
              </th>
              <th
                className="text-center p-1.5 text-[13px] w-[80px] border border-gray-300"
                scope="col"
              >
                Emp ID
              </th>
              <th
                className="text-center p-1.5 text-[13px] w-[120px] border border-gray-300"
                scope="col"
              >
                State
              </th>
              <th
                className="text-center p-1.5 text-[13px] w-[180px] border border-gray-300"
                scope="col"
              >
                Employee Name
              </th>
              <th
                className="text-center p-1.5 text-[13px] w-[140px] border border-gray-300"
                scope="col"
              >
                Designation
              </th>
              <th
                className="text-center p-1.5 text-[13px] w-[120px] border border-gray-300"
                scope="col"
              >
                City/HQ
              </th>
              <th
                className="text-center p-1.5 text-[13px] w-[120px] border border-gray-300"
                scope="col"
              >
                Division
              </th>
              <th
                className="text-center p-1.5 text-[13px] w-[90px] border border-gray-300"
                scope="col"
              >
                DOJ
              </th>
              <th
                className="text-center p-1.5 text-[13px] w-[180px] border border-gray-300"
                scope="col"
              >
                Manager
              </th>
              <th
                className="text-center p-1.5 text-[13px] w-[90px] border border-gray-300"
                scope="col"
              >
                DOR
              </th>

              {generateDayHeaders()}
              <th
                className="text-center p-1.5 text-[13px] w-[80px] border border-gray-300"
                scope="col"
              >
                Total
              </th>
            </tr>
          </thead>

          <tbody>
            {data.map((row, index) => (
              <tr
                key={index}
                className={`text-left text-[13px] ${
                  index % 2 === 0
                    ? "text-[#333333] bg-[#F7F6F3]"
                    : "text-[#284775] bg-white"
                }`}
              >
                {/* S.No */}
                <td className="text-center p-1.5 w-[40px] border border-gray-300">
                  {index + 1}
                </td>

                {/* Emp ID */}
                <td className="text-left p-1.5 w-[80px] border border-gray-300">
                  <span>
                    {row.user_id || ""}
                  </span>
                </td>

                {/* State */}
                <td className="text-left p-1.5 w-[120px] border border-gray-300">
                  <span
                    title={row.state}
                    className="block overflow-hidden text-ellipsis whitespace-nowrap"
                  >
                    {row.state || row.state_name || ""}
                  </span>
                </td>

                {/* Employee Name */}
                <td className="text-left p-1.5 w-[180px] border border-gray-300">
                  <span
                    title={
                      row.employee_name ||
                      `${row.first_name || ""} ${row.last_name || ""}`.trim()
                    }
                    className="block overflow-hidden text-ellipsis whitespace-nowrap"
                  >
                    {row.employee_name ||
                      `${row.first_name || ""} ${row.last_name || ""}`.trim() ||
                      ""}
                  </span>
                </td>

                {/* Designation */}
                <td className="text-left p-1.5 w-[140px] border border-gray-300">
                  <span
                    title={row.designation || row.job_title}
                    className="block overflow-hidden text-ellipsis whitespace-nowrap"
                  >
                    {row.designation || row.job_title || "-"}
                  </span>
                </td>

                {/* City/HQ */}
                <td className="text-left p-1.5 w-[120px] border border-gray-300">
                  <span
                    title={row.city_hq || row.hq || row.headquarter_name}
                    className="block overflow-hidden text-ellipsis whitespace-nowrap"
                  >
                    {row.city_hq || row.hq || row.headquarter_name || ""}
                  </span>
                </td>

                {/* Division */}
                <td className="text-left p-1.5 w-[120px] border border-gray-300">
                  <span
                    title={row.division || row.division_name}
                    className="block overflow-hidden text-ellipsis whitespace-nowrap"
                  >
                    {row.division || row.division_name || ""}
                  </span>
                </td>

                {/* DOJ */}
                <td className="text-left p-1.5 w-[90px] border border-gray-300">
                  <span>
                    {formatDate(new Date(row.doj))}
                  </span>
                </td>

                {/* Manager */}
                <td className="text-left p-1.5 w-[180px] border border-gray-300">
                  <span
                    title={row.manager || row.manager_name}
                    className="block overflow-hidden text-ellipsis whitespace-nowrap"
                  >
                    {row.manager || row.manager_name || "N/A"}
                  </span>
                </td>


                {/* DOR */}
                <td className="text-left p-1.5 w-[90px] border border-gray-300">
                  <span>
                    {row.dor
                      ? formatDate(new Date(row.dor))
                      : ""}
                  </span>
                </td>

                {/* Dynamic day cells */}
                {generateDayCells(row)}

                {/* Total */}
                <td className="text-center p-1.5 w-[80px] border border-gray-300">
                  <a
                    onClick={(e) => e.preventDefault()}
                    href="#"
                    className="text-blue-700"
                  >
                    {row.net_total_qty || row.total_qty || 0}
                  </a>
                </td>
              </tr>
            ))}

            {data.length > 0 && (
              <tr className="text-center text-white bg-[#5D7B9D] font-bold">
                {Array.from({ length: 10 + daysInMonth + 1 }).map((_, i) => (
                  <td key={i} className="p-1.5 border border-gray-300">
                    &nbsp;
                  </td>
                ))}
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {selectedEmployee && selectedDate && (
        <ProductSalesModal
          employeeId={selectedEmployee}
          date={selectedDate}
          onClose={closeModal}
        />
      )}
    </>
  );
};

export default KPKBProductList;
